// sales configurator / orders — shared Zod schemas + types (Worker validates, SPA reuses).
// draft fields are permissive (partial drafts allowed); confirmation enforces required fields
// server-side. the committed snapshot is the canonical immutable history.
import { z } from 'zod';
export const ORDER_STATUSES = [
    'draft',
    'pending',
    'confirmed',
    'changed',
    'locked',
    'cancelled',
    'deleted',
    'completed',
];
const str = (max) => z.string().max(max).optional().nullable();
export const OrderBuyerSchema = z.object({
    name: str(200),
    registeredAddress: str(400),
    vatTaxCode: str(80),
    profession: str(120),
    email: z.string().max(200).optional().nullable(),
    telephone: str(60),
    // link to the customer master (CRM). the snapshot still stores the buyer text above so the
    // committed revision stays immutable even if the customer record later changes.
    customerId: z.number().int().positive().optional().nullable(),
});
export const OrderDeliverySchema = z.object({
    method: str(120),
    destination: str(200),
    estimatedDeliveryPeriod: str(80),
    requestedDeliveryDate: str(40),
    comments: str(2000),
});
export const OrderVesselSchema = z.object({
    modelId: z.number().int().positive().optional().nullable(),
    modelYear: z.number().int().optional().nullable(),
    flag: str(80),
    vesselName: str(160),
    sapNumber: str(50), // '?' or blank allowed until assigned
    hullNumber: str(50),
});
export const OrderEquipmentSchema = z.object({
    equipmentId: z.number().int().positive(),
    selected: z.boolean().optional().default(false),
    removedFromStandard: z.boolean().optional().default(false),
    quantity: z.number().int().min(0).max(9999).optional().default(1),
    internalPrice: z.number().optional().nullable(),
    dealerPrice: z.number().optional().nullable(),
    customerPrice: z.number().optional().nullable(),
    discount: z.number().optional().nullable(),
    color: str(120),
    drawingReference: str(200),
    customerComment: str(1000),
    productionComment: str(1000),
    lowerUnit: str(80),
    esEh: str(80),
    propellers: str(120),
    premier: str(80),
    tieBar: str(80),
    preRigDelivery: str(80),
    enginesDelivery: str(80),
});
export const CustomItemSchema = z.object({
    title: z.string().min(1).max(200),
    description: str(2000),
    category: str(120),
    quantity: z.number().int().min(0).max(9999).optional().default(1),
    customerComment: str(1000),
    productionComment: str(1000),
});
export const ProductionRequirementSchema = z.object({
    category: str(120),
    description: z.string().min(1).max(2000),
    source: str(60),
});
export const OrderDraftSchema = z.object({
    proformaNumber: str(80),
    orderDate: str(40),
    salesRep: str(120),
    dealer: str(160),
    internalComments: str(4000),
    buyer: OrderBuyerSchema.optional().default({}),
    delivery: OrderDeliverySchema.optional().default({}),
    vessel: OrderVesselSchema.optional().default({}),
    equipment: z.array(OrderEquipmentSchema).max(1000).optional().default([]),
    customItems: z.array(CustomItemSchema).max(200).optional().default([]),
    productionRequirements: z.array(ProductionRequirementSchema).max(200).optional().default([]),
});
export const CreateDraftSchema = z.object({ draft: OrderDraftSchema });
export const UpdateDraftSchema = z.object({ id: z.number().int().positive(), draft: OrderDraftSchema });
export const EditOrderSchema = z.object({
    id: z.number().int().positive(),
    draft: OrderDraftSchema,
    note: z.string().max(2000).optional(),
});
export const ConfirmOrderSchema = z.object({
    id: z.number().int().positive(),
    note: z.string().max(2000).optional(),
});
export const OrderIdSchema = z.object({ id: z.number().int().positive() });
export const LockOrderSchema = z.object({ id: z.number().int().positive(), locked: z.boolean() });
export const RequirementActionSchema = z.object({
    id: z.number().int().positive(),
    action: z.enum(['ack', 'unack', 'complete', 'incomplete']),
});
export const ExportLogSchema = z.object({
    id: z.number().int().positive(),
    revisionId: z.number().int().positive(),
    filename: z.string().min(1).max(300),
    exportType: z.string().max(40).optional().default('production_list'),
});
// Identity-only edit — allowed even while an order is locked (Customer / vessel name / SAP).
export const QuickEditIdentitySchema = z.object({
    id: z.number().int().positive(),
    customer: z.string().max(200).optional(),
    vesselName: z.string().max(160).optional(),
    sapNumber: z.string().max(50).optional(),
});
export const DEALER_DISCOUNT_PCT = 20;
export const VAT_PCT = 24;
const round2 = (n) => Math.round(n * 100) / 100;
/** canonical order pricing. matches the ALPHA 40 pricelist: the dealer discount applies ONLY to the
 *  discount-eligible subtotal (vessel + engine + eligible options = "SUBTOTAL A"); bespoke features
 *  and transport/services are billed at full price. VAT is charged on the net. used by BOTH the
 *  configurator (live total) and the server snapshot (frozen total) so the two agree to the cent.
 *    net   = eligible × (1 − discount%) + nonEligible
 *    total = net × (1 + VAT%)  */
export function orderPricing(eligibleOptional, nonEligibleOptional = 0) {
    const eligible = round2(eligibleOptional);
    const nonEligible = round2(nonEligibleOptional);
    const subtotalOptional = round2(eligible + nonEligible);
    const discount = round2((eligible * DEALER_DISCOUNT_PCT) / 100); // discount on eligible only
    const net = round2(subtotalOptional - discount);
    const vat = round2((net * VAT_PCT) / 100);
    return {
        subtotalStandard: 0,
        subtotalOptional,
        subtotalServices: nonEligible,
        discountPct: DEALER_DISCOUNT_PCT,
        discount,
        net,
        vatPct: VAT_PCT,
        vat,
        total: round2(net + vat),
        commission: 0,
    };
}
/** back-compat wrapper — treats the whole optional subtotal as discount-eligible. */
export function orderPricingFromOptional(optional) {
    return orderPricing(optional, 0);
}
/** Assembly-list export filename — one source of truth for the exporter AND the assembly orders
 *  "File" column (they had drifted). Blank/`?` SAP → NO-SAP. */
export function assemblyListFilename(sap, vessel, rev) {
    const s = sap && sap !== '?' ? sap : 'NO-SAP';
    return `${s} - ${vessel} - Assembly List - Rev ${rev}.xlsx`;
}
export const AcceptOrderSchema = z.object({ id: z.number().int().positive(), revision: z.number().int().positive() });
//# sourceMappingURL=orders.js.map